import { Alert, Box, Button, Container, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LinkLoader, { IncomeType } from '../../plaid/LinkLoader';
import { ProofButton } from '../../../ui/button/ProofButton';
import palette from '../../../ui/palette';
import { useUpload } from '.';

export default function DocUploadFallback() {
  const { t } = useTranslation();
  const {
    fallbackTriggered,
    lastFailedDocType,
    validationWarnings,
    pickBankStmtFallback,
    pickPaystubFallback,
    pickRetryUpload,
    continueAnyway,
    submitError,
    setSubmitError,
    goTo,
    goToTypes,
  } = useUpload();

  const [showContinueConfirm, setShowContinueConfirm] = useState(false);
  const [isSubmittingAnyway, setIsSubmittingAnyway] = useState(false);

  const onConfirmContinue = async () => {
    setIsSubmittingAnyway(true);
    try {
      await continueAnyway();
    } finally {
      setIsSubmittingAnyway(false);
      setShowContinueConfirm(false);
    }
  };

  // If a user lands here without ever triggering a fallback, send them back.
  useEffect(() => {
    if (!fallbackTriggered) goTo('select');
  }, [fallbackTriggered, goTo]);

  if (!fallbackTriggered) return null;

  // Show every applicable option on every failure. The only filter: don't show
  // "Upload [X]" when the user is already on [X], since Retry covers that case.
  const showUploadBankStmtOption = lastFailedDocType !== 'Bank Statement';
  const showUploadPaystubOption = lastFailedDocType !== 'Paystub';

  const titleKey =
    lastFailedDocType === 'Bank Statement'
      ? 'verification.type.document.fallback.titleBankStatement'
      : lastFailedDocType === 'Offer Letter'
        ? 'verification.type.document.fallback.titleOfferLetter'
        : 'verification.type.document.fallback.titlePaystub';
  // Retry is always available, so the "choose one" copy applies universally.
  const bodyKey = 'verification.type.document.fallback.bodyPaystub';

  const retryCtaKey =
    lastFailedDocType === 'Bank Statement'
      ? 'verification.type.document.fallback.retryCtaBankStatement'
      : lastFailedDocType === 'Offer Letter'
        ? 'verification.type.document.fallback.retryCtaOfferLetter'
        : 'verification.type.document.fallback.retryCtaPaystub';
  const retryHintKey =
    lastFailedDocType === 'Bank Statement'
      ? 'verification.type.document.fallback.retryHintBankStatement'
      : lastFailedDocType === 'Offer Letter'
        ? 'verification.type.document.fallback.retryHintOfferLetter'
        : 'verification.type.document.fallback.retryHintPaystub';

  return (
    <Container maxWidth="xs" disableGutters sx={{ py: { xs: 2, md: 3 } }}>
      <Typography variant="h2" textAlign="center" sx={{ fontWeight: 500, mb: 3 }}>
        {t(titleKey)}
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography sx={{ mb: validationWarnings.length > 0 ? 1.5 : 0, fontSize: 14 }}>{t(bodyKey)}</Typography>
        {validationWarnings.length > 0 && (
          <>
            <Typography sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}>{t('verification.type.document.fallback.filesAffected')}</Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, mb: 1.5 }}>
              {validationWarnings.map((w, i) => (
                <li key={`gate-warn-${i}`}>
                  <Typography sx={{ fontSize: 13 }}>
                    {w.fileName}: {w.message}
                  </Typography>
                </li>
              ))}
            </Box>
          </>
        )}
        <Typography sx={{ fontSize: 13, fontStyle: 'italic', color: palette.grey[60] }}>
          {t('verification.type.document.fallback.discardNotice')}
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mb: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <LinkLoader
            buttonText={t('verification.type.document.bankContinue')}
            income={true}
            incomeType={IncomeType.Bank}
            startIcon={<AccountBalanceIcon />}
          />
          <Typography variant="caption" sx={{ mt: -1, mb: 1, textAlign: 'center', color: palette.grey[60] }}>
            {t('verification.type.document.fallback.linkBankHint')}
          </Typography>
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <LinkLoader
            buttonText={t('verification.type.document.payrollContinue')}
            income={true}
            incomeType={IncomeType.Payroll}
            startIcon={<WorkOutlineIcon />}
          />
          <Typography variant="caption" sx={{ mt: -1, mb: 1, textAlign: 'center', color: palette.grey[60] }}>
            {t('verification.type.document.fallback.linkPayrollHint')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1.5 }}>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: palette.grey[30] }} />
          <Typography variant="body2" sx={{ color: palette.grey[60], fontSize: 12, fontWeight: 600, letterSpacing: 1 }}>
            {t('verification.type.document.fallback.or')}
          </Typography>
          <Box sx={{ flex: 1, height: '1px', backgroundColor: palette.grey[30] }} />
        </Box>

        <Box sx={{ mb: 2 }}>
          <ProofButton variant="text" fullWidth onClick={pickRetryUpload}>
            {t(retryCtaKey)}
          </ProofButton>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: palette.grey[60] }}>
            {t(retryHintKey)}
          </Typography>
        </Box>

        {showUploadPaystubOption && (
          <Box sx={{ mb: showUploadBankStmtOption ? 2 : 0 }}>
            <ProofButton variant="text" fullWidth onClick={pickPaystubFallback}>
              {t('verification.type.document.fallback.uploadPaystubCta')}
            </ProofButton>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: palette.grey[60] }}>
              {t('verification.type.document.fallback.uploadPaystubHint')}
            </Typography>
          </Box>
        )}

        {showUploadBankStmtOption && (
          <Box>
            <ProofButton variant="text" fullWidth onClick={pickBankStmtFallback}>
              {t('verification.type.document.fallback.uploadBankStmtCta')}
            </ProofButton>
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: palette.grey[60] }}>
              {t('verification.type.document.fallback.uploadBankStmtHint')}
            </Typography>
          </Box>
        )}
      </Box>

      {submitError && (
        <Alert severity="error" sx={{ mt: 3 }} onClose={() => setSubmitError('')}>
          <Typography sx={{ fontWeight: 'bold', fontSize: '14px' }}>{submitError}</Typography>
        </Alert>
      )}

      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Link component="button" type="button" onClick={() => setShowContinueConfirm(true)} underline="hover" sx={{ fontSize: 13 }}>
          {t('verification.type.document.fallback.continueAnywayLink')}
        </Link>
        <Link component="button" type="button" onClick={goToTypes} underline="hover" sx={{ fontSize: 13, color: palette.grey[60] }}>
          {t('verification.type.document.fallback.cancelLink')}
        </Link>
      </Box>

      <Dialog open={showContinueConfirm} onClose={() => !isSubmittingAnyway && setShowContinueConfirm(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ px: 3 }}>{t('verification.type.document.fallback.continueAnywayConfirmTitle')}</DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Typography>{t('verification.type.document.fallback.continueAnywayConfirmBody')}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button disabled={isSubmittingAnyway} onClick={() => setShowContinueConfirm(false)}>
            {t('verification.type.document.fallback.continueAnywayConfirmCancel')}
          </Button>
          <ProofButton variant="contained" disabled={isSubmittingAnyway} onClick={onConfirmContinue}>
            {t('verification.type.document.fallback.continueAnywayConfirmYes')}
          </ProofButton>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
